import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import WOSearchFields from "./WOSearchFields";

// One place to define an empty request, so a newly added field can't drift out
// of sync between "new", "clear" and "duplicate".
const emptyRequest = (requestId) => ({
  requestId,
  woId: "",
  customerName: "",
  jobName: "",
  subJobName: "",
  createdBy: "",
  locationId: "",
  // WO Details
  insideSalesPersonId: "",
  salesPersonId: "",
  paymentTermsId: "",
  taxRateId: "",
  shipmentMethod: "",
  invoiceReady: "",
  // Status
  WOStatus: "",
  adminReviewed: "",
  // Contact (stored inside the contactData JSON column)
  contactFirstName: "",
  contactLastName: "",
  contactDepartment: "",
  contactPhone: "",
  contactEmail: "",
  // Dates
  dueDateFrom: "",
  dueDateTo: "",
  woDateFrom: "",
  woDateTo: "",
  notes: "",
});

export default function WOAdvanceFormLayout({ showModal, setSearchResult }) {
  const dispatch = useDispatch();

  const [requestList, setRequestList] = useState([emptyRequest(1)]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const current = requestList[currentIndex];

  const nextId = () =>
    requestList.reduce((max, item) => Math.max(max, item.requestId), 0) + 1;

  const nextRequest = () => {
    if (currentIndex < requestList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const previousRequest = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const createNewRequest = () => {
    const updated = [...requestList, emptyRequest(nextId())];
    setRequestList(updated);
    setCurrentIndex(updated.length - 1);
  };

  const duplicateRequest = () => {
    const updated = [...requestList, { ...current, requestId: nextId() }];
    setRequestList(updated);
    setCurrentIndex(updated.length - 1);
    dispatch(
      setNotification({ message: "Request duplicated", type: "success" }),
    );
  };

  const deleteRequest = () => {
    if (requestList.length <= 1) {
      dispatch(
        setNotification({
          message: "Cannot delete the last request",
          type: "warning",
        }),
      );
      return;
    }
    const updated = requestList.filter((_, i) => i !== currentIndex);
    setRequestList(updated);
    if (currentIndex >= updated.length) setCurrentIndex(updated.length - 1);
    dispatch(setNotification({ message: "Request deleted", type: "success" }));
  };

  const clearCurrentRequest = () => {
    const updated = [...requestList];
    updated[currentIndex] = emptyRequest(current.requestId);
    setRequestList(updated);
    dispatch(setNotification({ message: "Request cleared", type: "success" }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setRequestList((prev) => {
      const updated = [...prev];
      updated[currentIndex] = { ...updated[currentIndex], [name]: value };
      return updated;
    });
  };

  const handleSearch = async () => {
    // A range with both ends given must not be inverted -- the backend would
    // happily return nothing and it would look like "no matches" instead of a
    // mistake.
    const badRange = requestList.find(
      (request) =>
        request.dueDateFrom &&
        request.dueDateTo &&
        new Date(request.dueDateTo) < new Date(request.dueDateFrom),
    );
    if (badRange) {
      dispatch(
        setNotification({
          message: "Due date 'To' must be on or after 'From'.",
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);
      const { data } = await API.post("/work-order/advance-search", requestList, {
        headers: authHeader(),
      });
      setSearchResult(data);
      showModal(false);
      if (Array.isArray(data) && data.length === 0) {
        dispatch(
          setNotification({
            message: "No work orders matched this find.",
            type: "warning",
          }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Keep the index in range if the list shrinks from under us.
  useEffect(() => {
    if (currentIndex > requestList.length - 1) {
      setCurrentIndex(Math.max(0, requestList.length - 1));
    }
  }, [requestList, currentIndex]);

  // Modal.Header / Modal.Body / Modal.Footer rather than one long div: the
  // parent Modal is `scrollable`, which scrolls the BODY only and pins the
  // header and footer. With everything in the body, the title, the
  // request pager and the Search button all scrolled away with the fields.
  // These are returned in a fragment and rendered as the Modal's direct
  // children, which is what react-bootstrap needs for that to work.
  return (
    <>
      <Modal.Header className="d-flex justify-content-between align-items-center gap-1 py-2">
        <h3 className="mt-0 mb-0">Build A Find</h3>
        <div className="d-flex align-items-center gap-1">
          <div className="me-3">
            Showing <span className="text-primary">{currentIndex + 1}</span> of{" "}
            <span className="text-primary">{requestList.length}</span>
          </div>

          <button
            onClick={createNewRequest}
            disabled={loading}
            className="btn btn-success btn-sm p-0 px-1"
            title="Create New Request"
          >
            <i className="mdi mdi-plus fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={duplicateRequest}
            disabled={loading}
            className="btn btn-warning btn-sm p-0 px-1"
            title="Duplicate Request"
          >
            <i className="mdi mdi-content-duplicate fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={clearCurrentRequest}
            disabled={loading}
            className="btn btn-info btn-sm"
            style={{ padding: "0.4rem 0.8rem" }}
            title="Clear Current Request"
          >
            <span className="fs-5 fw-bold">C</span>
          </button>
          <button
            onClick={deleteRequest}
            disabled={requestList.length <= 1 || loading}
            className="btn btn-danger btn-sm p-0 px-1"
            title="Delete Request"
          >
            <i className="mdi mdi-delete fs-3 py-0 my-0"></i>
          </button>

          <div
            className="border-start border-3 mx-2"
            style={{ height: "35px", width: "1px" }}
          ></div>

          <div className="d-flex align-items-center gap-1">
            <button
              onClick={previousRequest}
              disabled={currentIndex <= 0 || loading}
              className="btn btn-success btn-sm p-0 px-1"
              title="Previous Request"
            >
              <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
            </button>
            <button
              onClick={nextRequest}
              disabled={currentIndex >= requestList.length - 1 || loading}
              className="btn btn-success btn-sm p-0 px-1"
              title="Next Request"
            >
              <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
            </button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-3 py-2">
        {loading && <Spinner />}

        <WOSearchFields
          formData={current}
          handleInputChange={handleInputChange}
        />
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end gap-1 py-2">
        <button onClick={() => showModal(false)} className="btn btn-secondary">
          Close
        </button>
        <button
          onClick={handleSearch}
          disabled={loading}
          className="btn btn-primary"
        >
          Search
        </button>
      </Modal.Footer>
    </>
  );
}
